import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  deletePipeline,
  getPipelinebyMission,
} from '../../redux/pipelines/pipelineActions'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import { formatTitle } from '../../utils/formatTitle'
import { Pipeline } from '../../types/pipelineTypes'
import {
  DetailEntityPanel,
  DetailTable,
} from '../../components/common/detail'
import {
  canManageMission,
  DetailRowActions,
} from '../../components/common/detail/DetailRowActions'
import {
  ListPageDateCell,
} from '../../components/common/ListPageCells'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import '../../components/common/ListPageTable.css'
import i18n from '../../i18n'

type Props = {
  IdMission: string
  missionName?: string
  onCountChange?: (count: number) => void
}

const PAGE_SIZE = 100

const PipelineListByMission: React.FC<Props> = ({
  IdMission,
  missionName,
  onCountChange,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const roles = useSelector((state: any) => state.auth.roles)
  const loading = useSelector((state: RootState) => state.pipeline.loading)
  const response = useSelector(
    (state: RootState) => state.pipeline.PipelineResponse
  )
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (IdMission) {
      dispatch(
        getPipelinebyMission({
          MissionId: IdMission,
          page: 1,
          pageSize: PAGE_SIZE,
        })
      )
    }
  }, [dispatch, IdMission])

  const rows = useMemo(() => {
    const list = response?.pipelines ?? []
    const q = search.trim().toLowerCase()
    if (!q) return list
    return list.filter((p) =>
      (p.PipelineName || '').toLowerCase().includes(q)
    )
  }, [response?.pipelines, search])

  useEffect(() => {
    onCountChange?.(response?.total ?? rows.length)
  }, [onCountChange, response?.total, rows.length])

  const canAdd = canManageMission(roles)

  return (
    <DetailEntityPanel
      listTitle={t('missionDetailsPage.sections.pipelineList', {
        defaultValue: 'Pipeline list',
      })}
      searchPlaceholder={t('filterByName', { defaultValue: 'Filter by name' })}
      searchValue={search}
      onSearchChange={setSearch}
      addLabel={t('buttons.addPipeline', { defaultValue: 'Add New Pipeline' })}
      onAdd={() => navigate(`/addpipeline`)}
      canAdd={canAdd}
    >
      <DetailTable<Pipeline>
        loading={loading && !response}
        loadingLabel={t('loading')}
        emptyTitle={t('missionDetailsPage.empty.title', {
          defaultValue: 'Nothing here yet',
        })}
        emptyMessage={t('missionDetailsPage.empty.pipelines', {
          defaultValue:
            'No pipeline is attached to this mission yet. Create one to sequence projects.',
        })}
        rows={rows}
        rowKey={(r) => r._id as string}
        columns={[
          {
            key: 'name',
            header: t('tables.pipelineTable.pipelineName', {
              defaultValue: 'Pipeline name',
            }),
            render: (r) => (
              <NavLink
                to={`/mission/${IdMission}/pipeline/${r._id}`}
                state={{
                  missionId: IdMission,
                  missionLabel: missionName
                    ? formatTitle(missionName)
                    : undefined,
                  pipelineId: r._id,
                  pipelineLabel: r.PipelineName
                    ? formatTitle(r.PipelineName)
                    : undefined,
                }}
                className="incyia-name-link"
              >
                <ListPageDescription
                  value={
                    r.PipelineName ? formatTitle(r.PipelineName) : undefined
                  }
                  tone="primary"
                />
              </NavLink>
            ),
          },
          {
            key: 'date',
            header: t('tables.pipelineTable.creationDate', {
              defaultValue: 'Creation date',
            }),
            render: (r) => (
              <ListPageDateCell
                value={(r as any).CreationDate}
                locale={i18n.language}
              />
            ),
          },
          {
            key: 'mission',
            header: t('tables.pipelineTable.missionName', {
              defaultValue: 'Mission name',
            }),
            render: (r) => (
              <ListPageDescription
                value={r.MissionName || missionName}
              />
            ),
          },
        ]}
        actionsHeader={t('Actions', { defaultValue: 'Action' })}
        renderActions={(r) => (
          <DetailRowActions
            roles={roles}
            onEdit={() => navigate(`/pipeline/${r._id}`)}
            onDelete={() => dispatch(deletePipeline(r._id as string))}
            deleteItem={{
              entityType: t('Pipelines', { defaultValue: 'Pipeline' }),
              name: r.PipelineName
                ? formatTitle(r.PipelineName)
                : undefined,
              details: [
                {
                  label: t('deleteConfirm.fields.mission'),
                  value: r.MissionName || missionName || '—',
                },
              ],
            }}
          />
        )}
      />
    </DetailEntityPanel>
  )
}

export default PipelineListByMission
